'use client';

import classnames from 'classnames';

import type { BaseComponentProps } from '../../base-component.types';
import { alphabet } from '../utils';

import styles from './Option.module.scss';

/**
 * How the choice is drawn: a card to press while answering on screen, the printed list a
 * sitting answered on paper reads as, or the plain line a reviewed answer is marked up on.
 */
type OptionAppearance = 'card' | 'paper' | 'plain';

/**
 * What the choice turned out to be — picked while answering, or how it went once the
 * answer is being reviewed.
 */
type OptionState =
  | 'none'
  | 'selected'
  | 'correct'
  | 'incorrect'
  | 'skipped'
  | 'neutral';

type OptionProps = BaseComponentProps & {
  /** The choice's 1-based position, which is also what letters it. */
  id: number;
  text: string;
  appearance?: OptionAppearance;
  state?: OptionState;
  /**
   * Given, the choice is a button to press. Left out it is read as plain text rather
   * than as a button the keyboard stops at and a screen reader calls unavailable.
   */
  onSelect?: (value: number) => void;
};

/**
 * One choice of a multiple-choice question, however the question is being shown. Asking
 * and reviewing draw the same row of letter and text; they differ in whether it can be
 * pressed and in what its state colours it.
 */
function Option({
  style,
  className,
  id,
  text,
  appearance = 'card',
  state = 'none',
  onSelect,
}: OptionProps) {
  const optionClassName = classnames(
    styles['main'],
    styles[`main--${appearance}`],
    styles[`main--${state}`],
    className,
  );

  const content = (
    <>
      <span className={styles['label']}>{alphabet(id - 1)}</span>
      <span className={styles['text']}>{text}</span>
    </>
  );

  if (!onSelect) {
    return (
      <div style={style} className={optionClassName}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      style={style}
      className={optionClassName}
      aria-pressed={state === 'selected'}
      onClick={() => onSelect(id)}
    >
      {content}
    </button>
  );
}

export { Option };
export type { OptionState };
